import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Users, Activity, AlertCircle, Clock, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WINDOWS = [
  { label: "Last 7 days", value: 7 },
  { label: "Last 30 days", value: 30 },
  { label: "Last 90 days", value: 90 },
];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4"
    >
      <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${accent || "bg-sky-50 text-sky-600"}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-slate-900 tabular-nums">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminSearchAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (window) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/search-analytics`, { params: { days: window } });
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
  }, [days]);

  return (
    <div data-testid="admin-search-analytics-page" className="bg-white min-h-full">
      {/* Header */}
      <div className="px-5 lg:px-8 py-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-900">
            <Search className="h-4 w-4 text-sky-600" />
            <h1 className="text-base font-bold">Search Analytics</h1>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            What visitors are searching for on the site. Use the "No-result" list to spot content gaps.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="analytics-window"
            className="h-9 px-2 text-xs border border-slate-300 rounded-md bg-white"
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(days)}
            data-testid="analytics-refresh"
            className="rounded-lg text-xs h-9"
          >
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      <div className="p-5 lg:p-8 space-y-6">
        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">
            {error}
          </div>
        )}

        {loading && !data ? (
          <p className="text-sm text-slate-400 text-center py-10">Loading…</p>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard icon={Activity} label="Total searches" value={data?.total_searches ?? 0} />
              <StatCard
                icon={Users}
                label="Unique visitors"
                value={data?.unique_visitors ?? 0}
                accent="bg-emerald-50 text-emerald-600"
              />
              <StatCard
                icon={AlertCircle}
                label="No-result queries"
                value={data?.no_result_queries?.length ?? 0}
                accent="bg-amber-50 text-amber-600"
              />
            </div>

            {/* Two columns: top queries + no-result queries */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel
                title="Top Queries"
                subtitle="Most-searched terms in this window."
                testId="analytics-top-queries"
                empty="No searches yet - share the site!"
                rows={data?.top_queries || []}
                columns={[
                  { key: "q", label: "Query", className: "font-semibold text-slate-900" },
                  { key: "count", label: "Count", align: "right" },
                  {
                    key: "avg_results",
                    label: "Avg results",
                    align: "right",
                    render: (v) => (v === 0 ? <span className="text-amber-600">{v}</span> : v),
                  },
                  { key: "unique_visitors", label: "Visitors", align: "right" },
                ]}
              />

              <Panel
                title="No-Result Queries"
                subtitle="People searched for these and got nothing - likely content gaps to address."
                testId="analytics-no-result-queries"
                empty="Great - every recent search returned results!"
                rows={data?.no_result_queries || []}
                columns={[
                  { key: "q", label: "Query", className: "font-semibold text-amber-700" },
                  { key: "count", label: "Times", align: "right" },
                  {
                    key: "last_seen",
                    label: "Last seen",
                    align: "right",
                    render: (v) => <span className="text-xs text-slate-500">{formatTime(v)}</span>,
                  },
                ]}
              />
            </div>

            {/* Recent feed */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">Recent Searches</h2>
              </div>
              {(data?.recent || []).length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-400 text-center">No recent searches yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="analytics-recent">
                  {(data?.recent || []).map((r, i) => (
                    <li key={`${r.created_at}-${i}`} className="px-4 py-2.5 flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-slate-900 font-medium truncate">{r.q}</span>
                        {r.result_count === 0 ? (
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            No results
                          </span>
                        ) : (
                          <span className="text-[10px] text-slate-500">{r.result_count} results</span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{formatTime(r.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="text-[11px] text-slate-400">
              Privacy: only the query string, a result count, and a one-way hashed IP fragment are stored - no raw IPs, emails or visitor identities.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Panel({ title, subtitle, testId, empty, rows, columns }) {
  return (
    <div data-testid={testId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</h2>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400 text-center">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-widest">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2 font-semibold ${c.align === "right" ? "text-right" : "text-left"}`}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={`${r.q}-${i}`} className="hover:bg-slate-50/50">
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 ${c.align === "right" ? "text-right tabular-nums" : "text-left"} ${c.className || "text-slate-700"}`}
                    >
                      {c.render ? c.render(r[c.key]) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
